import { useContext, useEffect, useState } from 'react'
import getItemIds from '@utilities/functions/getItemIds'
import getUpdatedItems from '@utilities/axios/getUpdatedItems'
import sortItems from '@utilities/functions/sortItems'
import { AuthContext } from '../contexts/AuthContext'
import _cloneDeep from 'lodash/cloneDeep'
import _isArray from 'lodash/isArray'
import hydrateWatchlist from '@utilities/functions/hydrateWatchlist'
import useDidMountEffect from '@utilities/hooks/useDidMountEffect'
import { GlobalUIContext } from '../contexts/GlobalUIContext'

type hookParams = {
  url: string
  initialData: any
  initItemIds?: number[]
  timeInterval?: number
  handleResponse?: any
  handleState?: any
  cap?: number
  initialLoadingState?: boolean
  isRefreshDisabled?: boolean
  needsHydration?: boolean
}

const useUpdatedItems = ({
  url,
  initialData,
  cap,
  initItemIds = getItemIds(initialData, cap),
  timeInterval = FIVE_SECONDS,
  handleState,
  handleResponse,
  initialLoadingState = false,
  isRefreshDisabled = false,
  needsHydration = false,
}: hookParams) => {
  const [data, setData] = useState<any>(sortItems(initialData, initItemIds))
  const [fetchUrl, setFetchUrl] = useState<string>(url)
  const [loading, setLoading] = useState<boolean>(initialLoadingState)
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const {
    userData: { watchlistItemIds },
    isAuthenticated,
    instance,
  } = useContext<any>(AuthContext)
  const { tabVisible } = useContext<any>(GlobalUIContext)
  const updateState = handleState || setData

  const getUnAuthItem = async () => {
    try {
      const { data: newData }: { data: any } = await instance.get(fetchUrl)

      return newData?.success === false ? data : newData
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (!tabVisible) return
    let itemIds = _cloneDeep(initItemIds)
    let currState = _cloneDeep(data)
    const fetch = async (fetchByIds: boolean) => {
      const responseHandler =
        !fetchByIds && handleResponse ? handleResponse : (items: any[]) => items

      const isFetchingAuthItems = !!(
        fetchByIds &&
        isAuthenticated &&
        initItemIds.length
      )
      let response = isFetchingAuthItems
        ? await getUpdatedItems({
            itemIds: itemIds,
            watchlistItemIds,
            instance,
          })
        : await getUnAuthItem()

      if (!response) return

      let newData = isFetchingAuthItems ? response : responseHandler(response)

      if (cap && _isArray(newData)) {
        newData = newData.slice(0, cap)
      }

      if (!newData) return

      currState =
        fetchByIds || needsHydration
          ? hydrate(newData, currState)
          : hydrateWatchlist(newData, watchlistItemIds)

      updateState(currState)
      setLastUpdated(new Date())
      if (!fetchByIds) {
        itemIds = getItemIds(responseHandler(response))
      }
      setLoading(false)

      if (needsHydration && !fetchByIds) {
        fetch(true)
      }
    }

    fetch(false)

    if (isRefreshDisabled) return

    const interval = setInterval(
      () => {
        fetch(true)
      },
      isAuthenticated ? timeInterval : FIVE_MINUTES
    )
    return () => clearInterval(interval)
  }, [fetchUrl, watchlistItemIds, instance, tabVisible, isAuthenticated])

  useDidMountEffect(() => {
    setLoading(true)
  }, [fetchUrl])

  const options: {
    fetchUrl: string
    setFetchUrl: any
    loading: boolean
    setLoading: any
    lastUpdated: Date
  } = {
    fetchUrl,
    setFetchUrl,
    loading,
    setLoading,
    lastUpdated,
  }

  return [
    data,
    (val: any) => {
      let items = sortItems(val, initItemIds)
      if (cap) {
        items = items.slice(0, cap)
      }
      setData(hydrate(items, data))
    },
    options,
  ]
}

export const hydrate = (newItems: any[] = [], items: any[] = []) => {
  if (!newItems.length) return []

  const newItemsHydrated = []

  for (let newItem of newItems) {
    // find the item with the same id in the items array
    let item = items.find((item) => item.id === newItem.id)
    // if the item exists, update it with the new properties
    if (item) {
      const freshItem = _cloneDeep(item)
      Object.assign(freshItem, newItem)
      newItemsHydrated.push(freshItem)
    } else {
      // otherwise, add the new item to the items array
      newItemsHydrated.push(newItem)
    }
  }
  return newItemsHydrated
}

const FIVE_SECONDS = 5 * 1000
const FIVE_MINUTES = 5 * 60 * 1000

export default useUpdatedItems
